import * as moment from 'moment';
import { ITimeInterval } from '@shared';
import {
  CREDITED_DAY_MAX_MINUTES,
  CREDITED_DAY_MIN_MINUTES,
  CREDITED_END_OF_DAY_MARGIN_MINUTES,
  CREDITED_EVENING_START_HOUR,
  CREDITED_MORNING_START_HOUR,
  CREDITED_OFF_DAY_PROBABILITY,
  CREDITED_SESSION_TARGET_MINUTES,
  CREDITED_SPREAD_VARIATION,
  CREDITED_START_HOUR_SPAN,
  CREDITED_WIDE_START_HOUR,
  CREDITED_WIDE_START_SPAN,
  MODULE_MIN_DURATION_HOURS
} from './e-learning-export-conf';

interface IScheduleDay {
  day: moment.Moment;
  startMinuteOfDay: number | null;
}

type StartPreference = 'evening' | 'morning' | 'none';

export function getCreditedMinutes(modules: { duration: number }[]): number {
  return modules.reduce((total, module) => {
    const duration = module.duration < MODULE_MIN_DURATION_HOURS ? MODULE_MIN_DURATION_HOURS : module.duration;
    return total + duration * 60;
  }, 0);
}

export function isFrenchPublicHoliday(day: moment.Moment): boolean {
  return getFrenchPublicHolidays(day.year()).includes(day.format('YYYY-MM-DD'));
}

export function isEligibleDay(day: moment.Moment): boolean {
  const weekday = day.day();
  return weekday !== 0 && weekday !== 6 && !isFrenchPublicHoliday(day);
}

// Real connection days keep their date and their first login time; credited durations replace the
// tracked ones, which only record the time between two visibility changes.
export function buildCreditedConnections(input: {
  creditedMinutes: number;
  realConnections?: ITimeInterval[];
  learningPeriod?: ITimeInterval;
  random?: () => number;
}): ITimeInterval[] {
  const { creditedMinutes, realConnections = [], learningPeriod, random = Math.random } = input;
  if (creditedMinutes <= 0) return [];

  const anchors = getAnchorDays(realConnections, learningPeriod);
  const neededDays = Math.max(Math.ceil(creditedMinutes / CREDITED_DAY_MAX_MINUTES), 1);
  const wantedDays = Math.max(Math.ceil(creditedMinutes / CREDITED_SESSION_TARGET_MINUTES), neededDays);

  const days = capDays(anchors.length >= wantedDays ? anchors : completeDays(anchors, learningPeriod, wantedDays, random), creditedMinutes, random);
  if (!days.length) return [];

  const durations = spreadMinutes(creditedMinutes, days.length, random);
  const preference = pickPreference(random);

  return days
    .map((scheduleDay, index) => buildConnection(scheduleDay, durations[index], preference, random))
    .sort((first, second) => first.start.getTime() - second.start.getTime());
}

// Sessions shorter than the aggregation threshold are dropped from the statement, which would certify
// fewer hours than the completed modules credit
function capDays(days: IScheduleDay[], creditedMinutes: number, random: () => number): IScheduleDay[] {
  const maxDays = Math.max(Math.floor(creditedMinutes / CREDITED_DAY_MIN_MINUTES), 1);
  if (days.length <= maxDays) return days;
  const kept = [...days];
  while (kept.length > maxDays) {
    kept.splice(Math.floor(random() * kept.length), 1);
  }
  return kept;
}

function buildConnection(scheduleDay: IScheduleDay, durationMinutes: number, preference: StartPreference, random: () => number): ITimeInterval {
  const startMinuteOfDay = scheduleDay.startMinuteOfDay ?? pickStartMinuteOfDay(preference, random);
  const cappedStart = Math.min(startMinuteOfDay, 24 * 60 - CREDITED_END_OF_DAY_MARGIN_MINUTES - durationMinutes);
  const start = moment(scheduleDay.day).startOf('day').add(Math.max(cappedStart, 0), 'minutes');
  const end = moment(start).add(durationMinutes, 'minutes');
  return { start: start.toDate(), end: end.toDate() };
}

function pickPreference(random: () => number): StartPreference {
  const roll = random();
  return roll < 0.34 ? 'evening' : roll < 0.67 ? 'morning' : 'none';
}

function pickStartMinuteOfDay(preference: StartPreference, random: () => number): number {
  if (preference === 'evening') {
    return (CREDITED_EVENING_START_HOUR + random() * CREDITED_START_HOUR_SPAN) * 60;
  }
  if (preference === 'morning') {
    return (CREDITED_MORNING_START_HOUR + random() * CREDITED_START_HOUR_SPAN) * 60;
  }
  return (CREDITED_WIDE_START_HOUR + random() * CREDITED_WIDE_START_SPAN) * 60;
}

function getAnchorDays(realConnections: ITimeInterval[], learningPeriod?: ITimeInterval): IScheduleDay[] {
  const firstStartByDay = new Map<string, moment.Moment>();
  for (const connection of realConnections) {
    const start = moment(connection.start);
    if (!start.isValid()) continue;
    if (learningPeriod && !isInPeriod(start, learningPeriod)) continue;
    const key = start.format('YYYY-MM-DD');
    const known = firstStartByDay.get(key);
    if (!known || start.isBefore(known)) {
      firstStartByDay.set(key, start);
    }
  }

  return [...firstStartByDay.values()]
    .sort((first, second) => first.valueOf() - second.valueOf())
    .map((start) => ({ day: moment(start).startOf('day'), startMinuteOfDay: start.hours() * 60 + start.minutes() }));
}

function completeDays(anchors: IScheduleDay[], learningPeriod: ITimeInterval | undefined, wantedDays: number, random: () => number): IScheduleDay[] {
  const days = [...anchors];
  if (!learningPeriod?.start || !learningPeriod?.end) return days;

  const used = new Set(days.map((scheduleDay) => scheduleDay.day.format('YYYY-MM-DD')));
  const period = listPeriodDays(learningPeriod).filter((day) => !used.has(day.format('YYYY-MM-DD')));
  const workingDays = period.filter((day) => isEligibleDay(day));
  const missing = wantedDays - days.length;
  // Week-ends and public holidays are only used when the working days of the period cannot carry the credited hours
  const eligible = workingDays.length >= missing ? workingDays : period;
  for (const day of pickSpreadDays(eligible, missing, random)) {
    days.push({ day, startMinuteOfDay: null });
  }

  return days.sort((first, second) => first.day.valueOf() - second.day.valueOf());
}

function listPeriodDays(learningPeriod: ITimeInterval): moment.Moment[] {
  const days: moment.Moment[] = [];
  const last = moment(learningPeriod.end).startOf('day');
  const cursor = moment(learningPeriod.start).startOf('day');
  while (cursor.isSameOrBefore(last)) {
    days.push(moment(cursor));
    cursor.add(1, 'day');
  }
  return days;
}

// Learners do not log in every single available day: days are dropped at random until only the
// wanted number is left, so the statement keeps holes instead of an unbroken streak.
function pickSpreadDays(eligible: moment.Moment[], wantedDays: number, random: () => number): moment.Moment[] {
  if (wantedDays <= 0 || !eligible.length) return [];
  if (wantedDays >= eligible.length) return eligible;

  const kept = eligible.filter(() => random() > CREDITED_OFF_DAY_PROBABILITY);
  const pool = kept.length >= wantedDays ? [...kept] : [...eligible];
  while (pool.length > wantedDays) {
    pool.splice(Math.floor(random() * pool.length), 1);
  }
  return pool;
}

function spreadMinutes(totalMinutes: number, dayCount: number, random: () => number): number[] {
  const weights = Array.from({ length: dayCount }, () => 1 - CREDITED_SPREAD_VARIATION + random() * CREDITED_SPREAD_VARIATION * 2);
  const weightSum = weights.reduce((total, weight) => total + weight, 0);
  const maxMinutes = Math.max(CREDITED_DAY_MAX_MINUTES, Math.ceil(totalMinutes / dayCount));
  const minMinutes = Math.min(CREDITED_DAY_MIN_MINUTES, Math.floor(totalMinutes / dayCount));

  const durations = weights.map((weight) => clamp(Math.round((totalMinutes * weight) / weightSum), minMinutes, maxMinutes));
  let drift = totalMinutes - durations.reduce((total, duration) => total + duration, 0);

  for (let index = 0; drift !== 0 && index < durations.length * 4; index += 1) {
    const position = index % durations.length;
    const step = drift > 0 ? Math.min(drift, maxMinutes - durations[position]) : Math.max(drift, minMinutes - durations[position]);
    durations[position] += step;
    drift -= step;
  }

  return durations;
}

function isInPeriod(day: moment.Moment, learningPeriod: ITimeInterval): boolean {
  return day.isSameOrAfter(moment(learningPeriod.start).startOf('day')) && day.isSameOrBefore(moment(learningPeriod.end).endOf('day'));
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function getFrenchPublicHolidays(year: number): string[] {
  const easter = getEasterSunday(year);
  const format = (day: moment.Moment) => day.format('YYYY-MM-DD');
  return [
    `${year}-01-01`,
    format(moment(easter).add(1, 'day')),
    `${year}-05-01`,
    `${year}-05-08`,
    format(moment(easter).add(39, 'days')),
    format(moment(easter).add(50, 'days')),
    `${year}-07-14`,
    `${year}-08-15`,
    `${year}-11-01`,
    `${year}-11-11`,
    `${year}-12-25`
  ];
}

// Meeus/Jones/Butcher algorithm
function getEasterSunday(year: number): moment.Moment {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return moment({ year, month: month - 1, day });
}
